import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import StraightenIcon from '@mui/icons-material/Straighten';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import QuizIcon from '@mui/icons-material/Quiz';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import ManIcon from '@mui/icons-material/Man';
import WomanIcon from '@mui/icons-material/Woman';
import DashboardStatCard from './DashboardStatCard';
import { useDashboardStats } from '../hooks/useDashboardStats';

export default function DashboardOverview() {
  const { loading, stats, errors, refresh, numberFormatter } = useDashboardStats();

  const totalGender = stats ? stats.malePatients + stats.femalePatients + stats.otherPatients : 0;
  const malePercent = totalGender ? (stats.malePatients / totalGender) * 100 : 0;
  const femalePercent = totalGender ? (stats.femalePatients / totalGender) * 100 : 0;

  const statCards = stats
    ? [
        {
          title: 'Pacientes registrados',
          value: numberFormatter.format(stats.totalPatients),
          subtitle: `Activos: ${numberFormatter.format(stats.activePatients)}${
            stats.inactivePatients ? ` · Inactivos: ${numberFormatter.format(stats.inactivePatients)}` : ''
          }`,
          icon: <PeopleAltIcon fontSize="large" />,
        },
        {
          title: 'Edad promedio',
          value: stats.averageAge ? `${stats.averageAge} anos` : 'Sin datos',
          subtitle: stats.totalPatients ? `Calculada sobre ${numberFormatter.format(stats.totalPatients)} pacientes` : undefined,
          icon: <CalendarMonthIcon fontSize="large" />,
        },
        {
          title: 'Entrevistas registradas',
          value: numberFormatter.format(stats.totalInterviews),
          subtitle: `${numberFormatter.format(stats.totalInterviews || 0)} entrevistas totales`,
          icon: <AssignmentIcon fontSize="large" />,
        },
        {
          title: 'Informes SPPB / TUG',
          value: numberFormatter.format(stats.totalCapacidades),
          subtitle: 'Ultimos registros de capacidad funcional',
          icon: <DirectionsRunIcon fontSize="large" />,
        },
        {
          title: 'Fuerza de prension',
          value: numberFormatter.format(stats.totalValoraciones),
          subtitle: 'Valoraciones funcionales registradas',
          icon: <FitnessCenterIcon fontSize="large" />,
        },
        {
          title: 'Informes antropometricos',
          value: numberFormatter.format(stats.totalMedidas),
          subtitle: 'Mediciones antropometricas acumuladas',
          icon: <StraightenIcon fontSize="large" />,
        },
        {
          title: 'Registros 5STS / Encoder',
          value: numberFormatter.format(stats.totalEncoders),
          subtitle: 'Evaluaciones de potencia y velocidad',
          icon: <ElectricBoltIcon fontSize="large" />,
        },
        {
          title: 'Cuestionarios completados',
          value: numberFormatter.format(stats.totalCuestionarios),
          subtitle: 'Incluye Suelo Pelvico, Ventilacion y Tesis',
          icon: <QuizIcon fontSize="large" />,
        },
        stats.totalUsers !== null
          ? {
              title: 'Usuarios del sistema',
              value: numberFormatter.format(stats.totalUsers),
              subtitle:
                stats.activeUsers !== null
                  ? `Activos: ${numberFormatter.format(stats.activeUsers)} · Inactivos: ${numberFormatter.format(
                      stats.totalUsers - stats.activeUsers
                    )}`
                  : undefined,
              icon: <SupervisedUserCircleIcon fontSize="large" />,
            }
          : null,
      ].filter(Boolean)
    : [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" color="#0D6AEE">
              Dashboard general
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Visualiza de un vistazo los principales indicadores y la actividad reciente del sistema.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={refresh} disabled={loading}>
            Actualizar
          </Button>
        </Box>
      </Grid>

      {errors.length > 0 ? (
        <Grid item xs={12}>
          <Alert severity="warning">
            <Typography variant="subtitle2" gutterBottom>
              Algunos bloques no se pudieron cargar correctamente:
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {errors.map((message, index) => (
                <Box component="li" key={`dashboard-error-${index}`} sx={{ fontSize: 14 }}>
                  {message}
                </Box>
              ))}
            </Box>
          </Alert>
        </Grid>
      ) : null}

      {loading ? (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      ) : null}

      {stats ? (
        <>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={card.title}>
              <DashboardStatCard {...card} />
            </Grid>
          ))}

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ManIcon color="primary" />
                    <Typography variant="body2">Hombres</Typography>
                  </Box>
                  <Typography variant="body2">
                    {numberFormatter.format(stats.malePatients)} ({malePercent.toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={malePercent} sx={{ height: 9, borderRadius: 5, mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WomanIcon color="secondary" />
                    <Typography variant="body2">Mujeres</Typography>
                  </Box>
                  <Typography variant="body2">
                    {numberFormatter.format(stats.femalePatients)} ({femalePercent.toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={femalePercent}
                  sx={{ height: 9, borderRadius: 5, mb: stats.otherPatients ? 2 : 0 }}
                  color="secondary"
                />
                {stats.otherPatients ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin dato especificado: {numberFormatter.format(stats.otherPatients)} pacientes
                  </Typography>
                ) : null}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" color="#0D6AEE">
                Pacientes por grupo
              </Typography>
              <List dense disablePadding>
                {stats.groupBreakdown.length ? (
                  stats.groupBreakdown.slice(0, 10).map((group) => {
                    const percentage = stats.activePatients ? ((group.count / stats.activePatients) * 100).toFixed(1) : '0.0';
                    return (
                      <ListItem
                        key={group.key}
                        sx={{ py: 0.5 }}
                        secondaryAction={<Chip size="small" label={`${numberFormatter.format(group.count)} · ${percentage}%`} color="primary" variant="outlined" />}
                      >
                        <ListItemText primary={group.name} />
                      </ListItem>
                    );
                  })
                ) : (
                  <ListItem>
                    <ListItemText primary="No hay informacion disponible" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" color="#0D6AEE">
                Cuestionarios por tipo
              </Typography>
              <List dense disablePadding>
                {stats.cuestionariosByType.length ? (
                  stats.cuestionariosByType.map((entry) => {
                    const percentage = stats.totalCuestionarios ? ((entry.count / stats.totalCuestionarios) * 100).toFixed(1) : '0.0';
                    return (
                      <ListItem
                        key={entry.label}
                        sx={{ py: 0.5 }}
                        secondaryAction={<Chip size="small" label={`${numberFormatter.format(entry.count)} · ${percentage}%`} color="secondary" variant="outlined" />}
                      >
                        <ListItemText primary={entry.label} />
                      </ListItem>
                    );
                  })
                ) : (
                  <ListItem>
                    <ListItemText primary="Aun no se han registrado cuestionarios" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" color="#0D6AEE">
                Actividad reciente
              </Typography>
              <List dense disablePadding>
                {stats.recentRecords.length ? (
                  stats.recentRecords.map((item, index) => (
                    <React.Fragment key={`${item.type}-${index}`}>
                      {index > 0 ? <Divider component="li" /> : null}
                      <ListItem sx={{ alignItems: 'flex-start', py: 1 }}>
                        <ListItemText
                          primary={`${item.type} · ${item.label}`}
                          secondary={item.date.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                        />
                      </ListItem>
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="Sin registros recientes" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
        </>
      ) : !loading ? (
        <Grid item xs={12}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No hay datos para mostrar.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprueba que existan registros en el sistema o vuelve a intentarlo mas tarde.
            </Typography>
          </Paper>
        </Grid>
      ) : null}
    </Grid>
  );
}
